import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SaveModal.css';

// Types
import type { 
  GenreCategory, 
  DifficultyLevel,
  ProgressionMetadata
} from '../presets/presetSchema';

// Filter options (same as SearchFilters)
const GENRE_OPTIONS: GenreCategory[] = [
  'jazz', 'blues', 'rock', 'pop', 'folk', 'practice'
];

const DIFFICULTY_OPTIONS: DifficultyLevel[] = [
  'beginner', 'intermediate', 'advanced'
];

// Key options separated into major and minor, starting with C
const MAJOR_KEYS = [
  'C Major', 'C# Major', 'D Major', 'Eb Major',
  'E Major', 'F Major', 'F# Major', 'G Major',
  'Ab Major', 'A Major', 'Bb Major', 'B Major'
];

const MINOR_KEYS = [
  'C Minor', 'C# Minor', 'D Minor', 'Eb Minor',
  'E Minor', 'F Minor', 'F# Minor', 'G Minor',
  'Ab Minor', 'A Minor', 'Bb Minor', 'B Minor'
];

interface SaveModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (name: string, metadata: Partial<ProgressionMetadata>) => void;
  initialName: string;
  positioning?: 'centered' | 'dropdown'; // Add positioning option
}

export function SaveModal({ 
  isVisible, 
  onClose, 
  onSave, 
  initialName,
  positioning = 'centered' // Default to centered for backward compatibility
}: SaveModalProps) {
  // Basic fields
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  
  // Metadata fields
  const [genre, setGenre] = useState<GenreCategory | ''>('');
  const [difficulty, setDifficulty] = useState<DifficultyLevel | ''>('');
  const [key, setKey] = useState('');
  const [pattern, setPattern] = useState('');
  const [tags, setTags] = useState('');
  const [examples, setExamples] = useState('');
  const [favorite, setFavorite] = useState(false);
  
  // UI state
  const [showKeyDropdown, setShowKeyDropdown] = useState(false);
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  const [nameHasBeenTouched, setNameHasBeenTouched] = useState(false);
  
  // Refs for dropdown management
  const keyDropdownRef = useRef<HTMLDivElement>(null);
  const genreDropdownRef = useRef<HTMLDivElement>(null);
  const difficultyDropdownRef = useRef<HTMLDivElement>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isVisible) {
      setName(initialName);
      setDescription('');
      setGenre('');
      setDifficulty('');
      setKey('');
      setPattern('');
      setTags('');
      setExamples('');
      setFavorite(false);
      setShowKeyDropdown(false);
      setShowGenreDropdown(false);
      setShowDifficultyDropdown(false);
      setNameHasBeenTouched(false);
    }
  }, [isVisible, initialName]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (keyDropdownRef.current && !keyDropdownRef.current.contains(event.target as Node)) {
        setShowKeyDropdown(false);
      }
      if (genreDropdownRef.current && !genreDropdownRef.current.contains(event.target as Node)) {
        setShowGenreDropdown(false);
      }
      if (difficultyDropdownRef.current && !difficultyDropdownRef.current.contains(event.target as Node)) {
        setShowDifficultyDropdown(false);
      }
    };

    if (showKeyDropdown || showGenreDropdown || showDifficultyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showKeyDropdown, showGenreDropdown, showDifficultyDropdown]);

  // Handle dropdown selections
  const handleKeySelect = useCallback((selectedKey: string) => {
    setKey(selectedKey);
    setShowKeyDropdown(false);
  }, []);

  const handleGenreSelect = useCallback((selectedGenre: GenreCategory) => {
    setGenre(selectedGenre);
    setShowGenreDropdown(false);
  }, []);

  const handleDifficultySelect = useCallback((selectedDifficulty: DifficultyLevel) => {
    setDifficulty(selectedDifficulty);
    setShowDifficultyDropdown(false);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return; // Name is required
    }

    // Build metadata object
    const metadata: Partial<ProgressionMetadata> = {};
    
    if (description.trim()) metadata.description = description.trim();
    if (genre) metadata.genre = genre;
    if (difficulty) metadata.difficulty = difficulty;
    if (key) metadata.key = key;
    if (pattern.trim()) metadata.pattern = pattern.trim();
    if (favorite) metadata.favorite = favorite;
    
    // Parse tags (comma-separated)
    if (tags.trim()) {
      metadata.tags = tags.split(',').map(tag => tag.trim()).filter(Boolean);
    }
    
    // Parse examples (line-separated)
    if (examples.trim()) {
      metadata.examples = examples.split('\n').map(ex => ex.trim()).filter(Boolean);
    }

    onSave(name.trim(), metadata);
  }, [name, description, genre, difficulty, key, pattern, tags, examples, favorite, onSave]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit(e as any);
    }
  }, [onClose, handleSubmit]);

  if (!isVisible) return null;

  const isDropdown = positioning === 'dropdown';

  return (
    <div className={`save-modal-overlay ${isDropdown ? 'dropdown-positioning' : ''}`} onClick={onClose}>
      <div className={`save-modal-content ${isDropdown ? 'dropdown-content' : ''}`} onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h3>💾 Save Progression</h3>
          
          {/* Basic Fields */}
          <div className="save-field-row">
            <div className="save-field">
              <input
                id="progression-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setNameHasBeenTouched(true)}
                onKeyDown={handleKeyDown}
                placeholder="Your Progression Name"
                className={`save-input ${
                  nameHasBeenTouched 
                    ? (name.trim() ? 'valid' : 'invalid')
                    : ''
                }`}
                autoFocus
                required
              />
            </div>
            <div className="save-field save-star-field">
              <button
                type="button"
                className={`save-star-button ${favorite ? 'favorited' : ''}`}
                onClick={() => setFavorite(!favorite)}
                title={favorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                {favorite ? (
                  <svg width="25" height="25" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" 
                          stroke="currentColor" strokeWidth="0.5" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="25" height="25" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" 
                          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="save-field">
            <textarea
              id="progression-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="A brief description of this progression..."
              className="save-textarea"
              rows={2}
            />
          </div>

          {/* Metadata Fields */}
          <div className="save-field-row">
            <div className="save-field">
              <div className="save-dropdown" ref={genreDropdownRef}>
                <button
                  type="button"
                  className={`save-dropdown-button ${genre ? 'has-selection' : ''}`}
                  onClick={() => setShowGenreDropdown(!showGenreDropdown)}
                >
                  <span>{genre ? genre.charAt(0).toUpperCase() + genre.slice(1) : 'Genre'}</span>
                  <span className={`save-dropdown-arrow ${showGenreDropdown ? 'open' : ''}`}>▼</span>
                </button>
                
                {showGenreDropdown && (
                  <>
                    <div className="save-dropdown-overlay" onClick={() => setShowGenreDropdown(false)} />
                    <div className="save-dropdown-content">
                      <div className="save-dropdown-options">
                        {GENRE_OPTIONS.map((g) => (
                          <button
                            key={g}
                            type="button"
                            className={`save-dropdown-option ${genre === g ? 'selected' : ''}`}
                            onClick={() => handleGenreSelect(g)}
                          >
                            {g.charAt(0).toUpperCase() + g.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="save-field">
              <div className="save-dropdown" ref={difficultyDropdownRef}>
                <button
                  type="button"
                  className={`save-dropdown-button ${difficulty ? 'has-selection' : ''}`}
                  onClick={() => setShowDifficultyDropdown(!showDifficultyDropdown)}
                >
                  <span>{difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : 'Difficulty'}</span>
                  <span className={`save-dropdown-arrow ${showDifficultyDropdown ? 'open' : ''}`}>▼</span>
                </button>
                
                {showDifficultyDropdown && (
                  <>
                    <div className="save-dropdown-overlay" onClick={() => setShowDifficultyDropdown(false)} />
                    <div className="save-dropdown-content">
                      <div className="save-dropdown-options">
                        {DIFFICULTY_OPTIONS.map((d) => (
                          <button
                            key={d}
                            type="button"
                            className={`save-dropdown-option ${difficulty === d ? 'selected' : ''}`}
                            onClick={() => handleDifficultySelect(d)}
                          >
                            {d.charAt(0).toUpperCase() + d.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="save-field-row">
            <div className="save-field">
              <div className="save-dropdown" ref={keyDropdownRef}>
                <button
                  type="button"
                  className={`save-dropdown-button ${key ? 'has-selection' : ''}`}
                  onClick={() => setShowKeyDropdown(!showKeyDropdown)}
                >
                  <span>{key || 'Key'}</span>
                  <span className={`save-dropdown-arrow ${showKeyDropdown ? 'open' : ''}`}>▼</span>
                </button>
                
                {showKeyDropdown && (
                  <>
                    <div className="save-dropdown-overlay" onClick={() => setShowKeyDropdown(false)} />
                    <div className="save-dropdown-content">
                      <div className="key-grid-container">
                        <div className="key-section">
                          <div className="key-section-label">Major</div>
                          <div className="key-grid major-grid">
                            {MAJOR_KEYS.map((keyOption) => (
                              <button
                                key={keyOption}
                                type="button"
                                className={`key-option ${key === keyOption ? 'selected' : ''}`}
                                onClick={() => handleKeySelect(keyOption)}
                              >
                                {keyOption.replace(' Major', '')}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="key-section">
                          <div className="key-section-label">Minor</div>
                          <div className="key-grid minor-grid">
                            {MINOR_KEYS.map((keyOption) => (
                              <button
                                key={keyOption}
                                type="button"
                                className={`key-option ${key === keyOption ? 'selected' : ''}`}
                                onClick={() => handleKeySelect(keyOption)}
                              >
                                {keyOption.replace(' Minor', '')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="save-field">
              <input
                id="progression-pattern"
                type="text"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="vi IV I V"
                className="save-input"
              />
            </div>
          </div>

          {/* Tags Field */}
          <div className="save-field">
            <input
              id="progression-tags"
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Tags (comma-separated)"
              className="save-input"
            />
          </div>

          {/* Examples Field */}
          <div className="save-field">
            <textarea
              id="progression-examples"
              value={examples}
              onChange={(e) => setExamples(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Example songs (one per line)"
              className="save-textarea"
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="save-modal-buttons">
            <button 
              type="submit" 
              className="save-confirm-button"
              disabled={!name.trim()}
            >
              Save
            </button>
            <button 
              type="button" 
              className="save-cancel-button"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
